import {Component, OnInit} from '@angular/core';
import {DecimalPipe, Location} from '@angular/common';
import {HttpResponse} from '@angular/common/http';
import {ActivatedRoute, Router} from '@angular/router';
import {SubmissionHttpService} from '../data-access/submission-http.service';
import {FormHttpService} from '../data-access/form-http.service';
import {EmployeeHttpService} from '../../company/x-http-requests/employee-http.service';
import {isFileRef, Submission} from '../shared/models/submission.model';
import {FormDefinition} from '../shared/models/form-schema.model';
import {
  buildFieldLabelMap, buildFieldMetaMap, FieldMeta, formatFieldValue, FormattedFieldValue, groupSubmissionFields,
  hasReadableValue, isDataImage, LIST_VALUE_CLASSES, OrderedSubmissionField, submissionFieldId, SubmissionSection,
  orderedSubmissionFields
} from '../shared/services/submission-display';
import {buildFieldNoteMap, FieldNoteMap, FieldNotes, notesForField} from '../shared/services/static-text-notes';
import {FieldNoteComponent} from './field-note/field-note.component';
import {GlobalAlertService} from '../../../_services/global-alert.service';
import {AlertLevel} from '../../../enums/alert-level';
import {AuthenticationService} from '../../../_auth/authentication.service';
import {Roles} from '../../../_auth/x-enums/roles';

/**
 * Shows a single submission. Sensitive values arrive masked; a privileged user (SensitiveDataAccessor / platform
 * owner) gets a "reveal" action that re-fetches them decrypted (audited server-side). Attachments are listed as
 * metadata with a role-gated download. A republish that may have invalidated the submission shows a banner.
 */
@Component({
  selector: 'forms-submission-detail',
  standalone: true,
  imports: [DecimalPipe, FieldNoteComponent],
  template: `
    @if (submission; as s) {
    <div class="absolute inset-0 overflow-y-auto p-3 sm:p-4 lg:p-6">
      <div class="mx-auto w-full max-w-3xl flex flex-col gap-4">

        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center gap-3">
            <button type="button" (click)="back()" class="text-gray-400 hover:text-gray-700" aria-label="Zurück">
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
              </svg>
            </button>
            <h1 class="text-xl sm:text-2xl font-bold tracking-tight text-gray-900">Eingabe</h1>
          </div>
          <div class="flex flex-wrap items-center justify-end gap-2">
            @if (canReveal && hasSensitive(s) && !revealed) {
              <button (click)="reveal(s.id)"
                      class="inline-flex items-center gap-2 rounded-lg bg-theme-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-theme-primary-700 active:scale-95 transition-all">
                Sensible Daten anzeigen
              </button>
            }
            @if (revealed) {
              <span class="text-xs font-semibold text-theme-primary-700">Entschlüsselt angezeigt</span>
            }

            <!-- PDF export. Reveal-capable users choose masked vs. unmasked; everyone else gets a masked PDF. -->
            @if (canReveal) {
              <div class="relative">
                <button type="button" (click)="menuOpen = !menuOpen" [disabled]="downloading"
                        class="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 active:scale-95 transition-all disabled:opacity-50"
                        aria-haspopup="menu" [attr.aria-expanded]="menuOpen">
                  <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3M4 6h16M4 6v12a2 2 0 002 2h12a2 2 0 002-2V6"/>
                  </svg>
                  PDF
                </button>
                @if (menuOpen) {
                <div>
                  <!-- Click-away backdrop -->
                  <div class="fixed inset-0 z-10" (click)="menuOpen = false"></div>
                  <div role="menu"
                       class="absolute right-0 z-20 mt-2 w-56 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg">
                    <button type="button" role="menuitem" (click)="downloadDocument(false)"
                            class="block w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50">
                      Maskiert herunterladen
                    </button>
                    <button type="button" role="menuitem" (click)="downloadDocument(true)"
                            class="block w-full px-4 py-2.5 text-left text-sm font-semibold text-theme-primary-700 hover:bg-theme-primary-50">
                      Unmaskiert herunterladen
                    </button>
                  </div>
                </div>
                }
              </div>
            } @else {
              <button type="button" (click)="downloadDocument(false)" [disabled]="downloading"
                      class="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 active:scale-95 transition-all disabled:opacity-50">
                <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3M4 6h16M4 6v12a2 2 0 002 2h12a2 2 0 002-2V6"/>
                </svg>
                Als PDF herunterladen
              </button>
            }
          </div>
        </div>

        <!-- Needs-review banner -->
        @if (s.needsReview) {
        <div class="flex items-start gap-2 rounded-lg bg-yellow-50 border border-yellow-100 p-3">
          <svg class="h-4 w-4 mt-0.5 flex-shrink-0 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
          </svg>
          <p class="text-[11px] text-yellow-800 font-medium leading-relaxed">
            {{ s.reviewReason || 'Das Formular wurde geändert — bitte diese Eingabe prüfen.' }}
          </p>
        </div>
        }

        <!--
          Field values, ordered like the form definition (matches the edit form). Fields the submission carries
          no value for are left out entirely (#470) — since hidden fields are no longer submitted, a "–" row
          almost always meant "this question did not apply" and buried the real answers.
        -->
        @for (section of sections; track section.id) {
        <div class="bg-white rounded-lg border border-gray-100 shadow-sm divide-y divide-gray-100">
          @if (section.title) {
            <h2 class="px-4 py-2 text-[10px] font-black text-gray-600 uppercase tracking-widest bg-gray-50/70">
              {{ section.title }}
            </h2>
          }
          @for (f of section.fields; track fieldId(f)) {
            @let n = notes(f);
            <!--
              A field row is: [ notes the author put BEFORE it ] · [ label | value ] · [ notes AFTER it ].

              The notes are siblings of the label/value line, not children of either column, so an explanation
              spans the full row width instead of being squeezed into whichever column it was nested in — which
              is what happened while it lived inside the value cell (it forced the label to wrap and cramped the
              prose beside it). Placement comes from the definition, see {@link buildFieldNoteMap}.
            -->
            <div class="px-4 py-3" [attr.data-field]="f.fieldKey">
              @for (note of n.before; track note.block.id) {
                <forms-field-note class="mb-1.5 block" data-note-placement="before" [note]="note.block"/>
              }

              <div class="flex items-start justify-between gap-4">
                <div class="min-w-0">
                  <span class="block break-words text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    {{ labelFor(f) }}
                    @if (f.isSensitive) {
                      <span class="ml-1 text-theme-primary-500">●</span>
                    }
                  </span>

                  @if (isFile(f.value)) {
                    <!-- Attachment -->
                    <span class="text-sm font-medium text-gray-900 break-words">
                      {{ f.value.fileName }}
                      <span class="text-gray-400">({{ f.value.sizeBytes | number }} B)</span>
                    </span>
                  } @else if (isSignature(f)) {
                    <!--
                      A drawn signature is an image, not a value: printing it verbatim dumped the base64 blob.
                      'block' states what the layout already relied on — the image is its own line, not inline
                      content sharing one with the label above it. 'mt-1' stays because here the label IS above.
                    -->
                    <img [src]="f.value" [alt]="'Unterschrift ' + labelFor(f)"
                         class="mt-1 block h-16 w-auto max-w-full rounded-md border border-gray-100 bg-white object-contain p-1"/>
                  } @else {
                    <!--
                      One shared formatter decides how an answer reads (#501): Ja/Nein instead of true/false, the
                      option's label instead of its wire value, and a multi-select as a real list instead of the
                      bracketed, quoted array a JSON.stringify used to print.
                    -->
                    @let v = formatted(f);
                    @if (v.kind === 'list') {
                      <!--
                        Same classes as the employee self-view, from the one shared constant — see
                        {@link LIST_VALUE_CLASSES}. 'list-outside' is what gives a wrapped option a hanging
                        indent (its second line continues under its own first, not under the bullet).

                        The old 'mt-0.5' is gone on purpose: a text value in this same column carries no top
                        margin, so the list started 2 px lower than every other answer for no reason.
                      -->
                      <ul [class]="listClasses"
                          [class.text-gray-900]="!f.isMasked" [class.text-gray-500]="f.isMasked">
                        @for (item of v.items; track $index) {
                          <li>{{ item }}</li>
                        }
                      </ul>
                    } @else {
                      <span class="text-sm font-semibold break-words"
                            [class.text-gray-900]="!f.isMasked" [class.font-mono]="f.isMasked"
                            [class.text-gray-500]="f.isMasked">
                        {{ v.text }}
                      </span>
                    }
                  }
                </div>

                @if (isFile(f.value) && canReveal) {
                  <button (click)="download(s.id, f.value.blobId, f.value.fileName)"
                          class="shrink-0 rounded-md bg-theme-primary-50 px-3 py-1.5 text-xs font-semibold text-theme-primary-700 hover:bg-theme-primary-100">
                    Herunterladen
                  </button>
                }
              </div>

              @for (note of n.after; track note.block.id) {
                <forms-field-note class="mt-1.5 block" data-note-placement="after" [note]="note.block"/>
              }
            </div>
          }
        </div>
        }
      </div>
    </div>
    }
  `
})
export class SubmissionDetailComponent implements OnInit {
  protected submission?: Submission;
  /** The submission's field values flattened + ordered like the definition (see {@link orderedSubmissionFields}). */
  protected fields: OrderedSubmissionField[] = [];
  /** Those fields grouped for display: the non-repeating answers, then one section per repeating row (#499). */
  protected sections: SubmissionSection[] = [];
  protected readonly fieldId = submissionFieldId;
  protected revealed = false;
  /** Whether the masked/unmasked PDF-export menu is open (reveal-capable users only). */
  protected menuOpen = false;
  /** True while a PDF export is in flight (disables the export controls). */
  protected downloading = false;
  protected readonly isFile = isFileRef;
  /** The `<ul>` classes a list answer renders with — shared with the employee self-view so they cannot drift (#501). */
  protected readonly listClasses = LIST_VALUE_CLASSES;
  /** The form definition, loaded best-effort to order the field display; null until/unless it resolves. */
  private definition: FormDefinition | null = null;
  /** `fieldKey → {type, options}`, so a value renders as its option label / `Ja` / a date rather than raw (#501). */
  private meta: Record<string, FieldMeta> = {};
  /** `fieldKey → human label`, so a row is headed "Art der Besteuerung" and not `art_der_besteuerung` (#501). */
  private labels: Record<string, string> = {};
  /** `fieldKey → the notes explaining it, split into before/after` (#501). Empty until the definition resolves. */
  private noteMap: FieldNoteMap = {};
  /** The submitting employee's display name, resolved best-effort for the PDF metadata block (empty if unknown). */
  private employeeName = '';

  constructor(
    private route: ActivatedRoute,
    private http: SubmissionHttpService,
    private forms: FormHttpService,
    private employees: EmployeeHttpService,
    private gas: GlobalAlertService,
    private auth: AuthenticationService,
    private location: Location,
    private router: Router) {
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) return;
    this.http.get(id).withLoadingIndicator().subscribe({
      next: s => {
        this.setSubmission(s);
        // Resolve the employee's name (best-effort) for the PDF metadata block; on error the PDF falls back to the id.
        if (s.employeeId) {
          this.employees.get(s.employeeId).subscribe({
            next: e => this.employeeName = `${e.firstName ?? ''} ${e.lastName ?? ''}`.trim(),
            error: () => { /* not resolvable (e.g. permissions) — the document shows the Mitarbeiter-ID instead */ }
          });
        }
        // Load the definition (best-effort) so the values display in definition order, like the edit form.
        this.forms.getDefinition(s.formId).subscribe({
          next: def => {
            this.definition = def;
            this.meta = buildFieldMetaMap(def);
            this.labels = buildFieldLabelMap(def);
            this.noteMap = buildFieldNoteMap(def);
            this.setSubmission(s);
          },
          error: () => { /* keep storage order */ }
        });
      },
      error: () => this.gas.createAndShow('Fehler', 'Eingabe konnte nicht geladen werden.', AlertLevel.error)
    });
  }

  /**
   * Store a submission and (re)compute its definition-ordered field list.
   *
   * Fields carrying no value are dropped (#470): since a hidden field is no longer submitted at all, an empty
   * value overwhelmingly means "this question did not apply", and a screen of "–" rows hid the real answers.
   */
  private setSubmission(s: Submission): void {
    this.submission = s;
    this.fields = orderedSubmissionFields(this.definition, s).filter(f => hasReadableValue(f.value));
    this.sections = groupSubmissionFields(this.definition, this.fields);
  }

  /** Whether this value is a drawn signature and should render as an image rather than as a base64 string. */
  protected isSignature(f: OrderedSubmissionField): boolean {
    return isDataImage(f.value);
  }

  /**
   * Return to wherever the user came from. Callers that link here from outside the submissions list (e.g. the
   * employee-details "Formulare" tab) pass a return URL via the navigation {@code state.back}; otherwise we go
   * back to the submissions list.
   */
  protected back(): void {
    const back = (this.location.getState() as { back?: string } | null)?.back;
    if (back) this.router.navigateByUrl(back);
    else this.router.navigate(['/company/forms/submissions']);
  }

  protected get canReveal(): boolean {
    const perms = this.auth.userValue?.permissions ?? [];
    return perms.includes(Roles.SensitiveDataAccessor) || perms.includes(Roles.PlatformOwner);
  }

  protected hasSensitive(s: Submission): boolean {
    return s.groups.some(g => g.values.some(v => v.isSensitive));
  }

  /**
   * The field's human label, falling back to its data key (#501).
   *
   * This view printed the bare `fieldKey` — so a manager read `art_der_besteuerung` where the employee's own
   * view, which has always resolved the label, read "Art der Besteuerung". Same complaint as an option shown by
   * its wire value, one level up: the reader is shown the schema instead of the form.
   *
   * The fallback matters and is not defensive padding: the definition is fetched best-effort (a failed fetch
   * leaves the map empty, and only the *latest* version comes back, which may no longer describe a field this
   * submission holds). A key is a poor label but it still names the answer; blank would lose it.
   */
  protected labelFor(f: OrderedSubmissionField): string {
    return this.labels[f.fieldKey] ?? f.fieldKey;
  }

  /**
   * How this answer reads, via the one shared formatter (#501).
   *
   * This used to be a local `display()` doing `String(value)` / `JSON.stringify(value)` — a near-identical copy
   * of the one in the employee self-view — which is exactly why a boolean printed `true`, a select printed
   * `pauschal_2` and a multi-select printed `["keines"]` in both places at once.
   */
  protected formatted(f: OrderedSubmissionField): FormattedFieldValue {
    return formatFieldValue(f.value, this.meta[f.fieldKey]);
  }

  /**
   * The static text the author attached to this field, split into the notes written *before* it and those
   * written *after* it, each in definition order (both empty when nothing explains the field).
   *
   * The split is the whole point: a note the author put above a question used to render below its answer, which
   * is how an instruction ("Tragen Sie hier … ein") ended up reading as a footnote to the value.
   */
  protected notes(f: OrderedSubmissionField): FieldNotes {
    return notesForField(this.noteMap, f.fieldKey);
  }

  protected reveal(id: string): void {
    this.http.reveal(id).withLoadingIndicator().subscribe({
      next: s => {
        this.setSubmission(s);
        this.revealed = true;
      },
      error: err => this.gas.createAndShow(
        'Nicht erlaubt',
        err?.status === 403 ? 'Keine Berechtigung zum Anzeigen.' : 'Anzeigen fehlgeschlagen.',
        AlertLevel.error)
    });
  }

  /**
   * Download this submission as a branded PDF. {@code unmasked} is only offered to reveal-capable users and is
   * gated again server-side (a 403 surfaces if the role is missing). The browser save uses the server's
   * `Content-Disposition` file name when exposed, falling back to a name built from the form title.
   */
  protected downloadDocument(unmasked: boolean): void {
    const id = this.submission?.id;
    if (!id || this.downloading) return;
    this.menuOpen = false;
    this.downloading = true;
    this.http.downloadDocument(id, unmasked, this.employeeName || undefined).withLoadingIndicator().subscribe({
      next: res => {
        const blob = res.body!;
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = this.fileNameFrom(res) ?? this.fallbackFileName(unmasked);
        a.click();
        URL.revokeObjectURL(a.href);
        this.downloading = false;
      },
      error: err => {
        this.downloading = false;
        this.gas.createAndShow(
          'Fehler',
          err?.status === 403 ? 'Keine Berechtigung für den unmaskierten Download.' : 'Download fehlgeschlagen.',
          AlertLevel.error);
      }
    });
  }

  /** The server-provided download name from `Content-Disposition` (null when the header isn't exposed via CORS). */
  private fileNameFrom(res: HttpResponse<Blob>): string | null {
    const cd = res.headers.get('content-disposition');
    const match = cd?.match(/filename\*?=(?:UTF-8'')?["']?([^;"'\r\n]+)/i);
    return match ? decodeURIComponent(match[1]) : null;
  }

  /** A reasonable client-side file name when the server's isn't available. */
  private fallbackFileName(unmasked: boolean): string {
    const title = (this.definition?.title ?? 'Eingabe').replace(/[^\p{L}\p{N}]+/gu, '_').replace(/^_+|_+$/g, '');
    return `${title || 'Eingabe'}${unmasked ? '_unmaskiert' : ''}.pdf`;
  }

  protected download(id: string, blobId: string, fileName: string): void {
    this.http.downloadFile(id, blobId).withLoadingIndicator().subscribe({
      next: res => {
        const blob = res.body!;
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(a.href);
      },
      error: () => this.gas.createAndShow('Fehler', 'Download fehlgeschlagen.', AlertLevel.error)
    });
  }
}
